import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {HttpStatusCode} from 'axios';
import {useTranslation} from 'react-i18next';
import {useDebugSettings} from '@/components/document/hooks/use-debug-settings';
import {resolveEffectiveModelVersion} from '@/components/document/model-version/resolve-debug-model-version';
import {useConfig} from '@/contexts/config';
import {SupportedLocale} from '@/initializers/i18next';
import {Toaster} from '@/initializers/toaster';
import {
  DocumentAttachmentType,
  DocumentFormPayload,
  FullDocument,
  FullDocumentReadyForSignature,
  PartialDocumentReadyForResubmittingSignature,
} from '@/interfaces/document';
import {
  createDocumentAttachmentUploadUrl,
  create,
  downloadAttachment,
  generateForm,
  getAll,
  getAttachments,
  getById,
  getForm,
  remove,
  removeForm,
  removeSentDocumentsHistory,
  submitForSignature,
  update,
  removeDocumentAttachment,
  cancelSignatureRequest,
  resubmitForSignature,
  retrySubmitForSignature,
  retryFinalSubmit,
  clone,
  ContractHolder,
  AllDocuments,
  LatestTransaction,
} from '@/repositories/beneva-api/endpoints/documents';
import {analyticsHandler} from '@/services/analytics/analytics-handler';
import {DetailedInvestorAccount, Investor} from '@/types/client-info';
import {mapAccountToCOMP, mapClientInfoToINVE} from '@/utils/client-info';
import {getDocumentTypeFromAccount} from '@/utils/document/get-document-type-from-account';
import {mapAttachmentIdFromFieldId} from '@/utils/document/serializer';
import {isForbiddenError} from '@/utils/error-handling';
import {getFileExtension} from '@/utils/get-file-extension';
import {useFormatFullName} from '../use-format-fullname';

const DOCUMENTS_QUERY_KEY = ['documents'];

const refetchDocuments = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({queryKey: DOCUMENTS_QUERY_KEY});
};

export const useDocuments = (options?: Partial<Omit<UseQueryOptions<AllDocuments>, 'queryKey' | 'queryFn'>>) => {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEY,
    queryFn: async () => {
      const {data} = await getAll();
      return data;
    },
    ...options,
  });
};

export const useDocument = (documentId: string) => {
  return useSuspenseQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, documentId],
    queryFn: () => {
      return getById(documentId);
    },
  });
};

export type UseCreateDocumentMutationReturnType = {
  docId: string;
  state: {
    isNewDocument: boolean;
    firstName?: string;
    lastName?: string;
  };
};

type UseCreateDocumentMutationParam = {
  advisorCode: string;
};

export const useCreateDocumentForNewClientMutationFn = () => {
  const {t} = useTranslation();
  const {model} = useConfig();
  const debugModelVersion = useDebugSettings(state => state.debugModelVersion);

  return async ({advisorCode}: UseCreateDocumentMutationParam): Promise<UseCreateDocumentMutationReturnType> => {
    const {data: document} = await create({
      type: t('components.new-document-creation.type'),
      name: t('components.new-document-creation.name'),
      modelVersion: resolveEffectiveModelVersion(model.version, debugModelVersion),
      sectionINVE: {},
      advisorCode,
    });

    return {
      docId: document.id,
      state: {
        isNewDocument: true,
      },
    };
  };
};

type UseCreateDocumentForExistingClientMutationParam = {
  advisorCode: string;
  investor: Investor;
  account?: DetailedInvestorAccount;
};

export const useCreateDocumentForExistingClientMutationFn = () => {
  const {t} = useTranslation();
  const formatFullName = useFormatFullName();
  const {model, domainValueResolver} = useConfig();
  const debugModelVersion = useDebugSettings(state => state.debugModelVersion);

  return async ({
    advisorCode,
    investor,
    account,
  }: UseCreateDocumentForExistingClientMutationParam): Promise<UseCreateDocumentMutationReturnType> => {
    const {data: document} = await create({
      type: getDocumentTypeFromAccount(account, domainValueResolver, t('components.new-document-creation.type')),
      name: formatFullName(investor.firstName, investor.lastName),
      modelVersion: resolveEffectiveModelVersion(model.version, debugModelVersion),
      sectionINVE: mapClientInfoToINVE(investor, account),
      sectionCOMP: account ? mapAccountToCOMP(account) : {},
      advisorCode,
    });

    return {
      docId: document.id,
      state: {
        isNewDocument: true,
        firstName: investor.firstName,
        lastName: investor.lastName,
      },
    };
  };
};

type UseCreateDocumentForRecentClientMutationParam = {
  latestTransaction: LatestTransaction;
  advisorCode: string;
  contractHolder: ContractHolder;
};

export const useCreateDocumentForRecentClientMutationFn = () => {
  return async ({
    latestTransaction,
    advisorCode,
    contractHolder,
  }: UseCreateDocumentForRecentClientMutationParam): Promise<UseCreateDocumentMutationReturnType> => {
    const {data: document} = await clone(latestTransaction.documentId, contractHolder.type, advisorCode);

    return {
      docId: document.id,
      state: {
        isNewDocument: true,
        firstName: contractHolder.firstName,
        lastName: contractHolder.lastName,
      },
    };
  };
};

type UpdateDocumentParams = {
  documentId: string;
  updatedDocument: Partial<FullDocument>;
};

type UpdateDocumentOptions = {
  preventRefetch?: boolean;
};

export const useUpdateDocument = ({preventRefetch = false}: UpdateDocumentOptions = {}) => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({documentId, updatedDocument}: UpdateDocumentParams) => {
      const {sectionUSER, sectionCONS, ...rest} = updatedDocument;
      return update(documentId, rest);
    },
    onSuccess: (_, {documentId}) => {
      if (preventRefetch) return;
      queryClient.invalidateQueries({queryKey: ['document', documentId]});
      refetchDocuments(queryClient);
    },
    onError: (_: Error, {documentId}) => {
      queryClient.invalidateQueries({queryKey: ['document', documentId]});
      Toaster.error(t('components.document.context.feedback-message.save-changes.error'));
    },
  });
};

export const useDeleteDocument = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => {
      return remove(documentId);
    },
    onSuccess: () => {
      Toaster.success(t('components.document-menu.document-list.item.feedback-message.delete.success'));
      refetchDocuments(queryClient);
    },
    onError: () => {
      Toaster.error(t('components.document-menu.document-list.item.feedback-message.delete.error'));
    },
  });
};

export const useSubmitDocumentForSignature = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      document,
      documentLocale,
      modelJsonVersion,
      signatureQuestion,
      signatureEmailSubject,
    }: {
      documentId: string;
      document: FullDocumentReadyForSignature;
      documentLocale: SupportedLocale;
      modelJsonVersion: string;
      signatureQuestion: string;
      signatureEmailSubject: string;
    }) => {
      return submitForSignature({
        documentId,
        document,
        documentLocale,
        modelJsonVersion,
        signatureQuestion,
        signatureEmailSubject,
      });
    },
    onSuccess: (_data, {documentId}) => {
      Toaster.success(t('components.document.context.feedback-message.submit-for-signature.success'));
      refetchDocuments(queryClient);
      analyticsHandler.webFormSubmitted({
        form_id: documentId,
      });
    },
    onError: error => {
      const feedbackMessage = t(
        `components.document.context.feedback-message.submit-for-signature.error.${isForbiddenError(error) ? 'forbidden' : 'default'}`,
      );
      Toaster.error(feedbackMessage);
    },
  });
};

export const useResubmitDocumentForSignature = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      serializedDocument,
      document,
    }: {
      documentId: string;
      serializedDocument: PartialDocumentReadyForResubmittingSignature;
      document: FullDocument;
    }) => {
      return resubmitForSignature({
        documentId,
        serializedDocument,
        document,
      });
    },
    onSuccess: (_data, {documentId}) => {
      Toaster.success(t('components.document-menu.document-list.item.feedback-message.resend.success'));
      refetchDocuments(queryClient);
      analyticsHandler.webFormSubmitted({
        form_id: documentId,
      });
    },
    onError: error => {
      const feedbackMessage = t(
        `components.document-menu.document-list.item.feedback-message.resend.error.${isForbiddenError(error) ? 'forbidden' : 'default'}`,
      );
      Toaster.error(feedbackMessage);
    },
  });
};

export const useRetryFinalSubmit = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => {
      return retryFinalSubmit(documentId);
    },
    onSuccess: () => {
      Toaster.success(t('components.document-menu.document-list.item.feedback-message.retry-final-submit.success'));
      refetchDocuments(queryClient);
    },
    onError: error => {
      const feedbackMessage = t(
        `components.document-menu.document-list.item.feedback-message.retry-final-submit.error.${isForbiddenError(error) ? 'forbidden' : 'default'}`,
      );
      Toaster.error(feedbackMessage);
    },
  });
};

export const useRetrySubmitForSignature = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => {
      return retrySubmitForSignature(documentId);
    },
    onSuccess: () => {
      Toaster.success(t('components.document-menu.document-list.item.feedback-message.submit-for-signature.success'));
      refetchDocuments(queryClient);
    },
    onError: error => {
      const feedbackMessage = t(
        `components.document-menu.document-list.item.feedback-message.submit-for-signature.error.${isForbiddenError(error) ? 'forbidden' : 'default'}`,
      );
      Toaster.error(feedbackMessage);
    },
  });
};

export const useCancelSignatureRequest = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => {
      return cancelSignatureRequest(documentId);
    },
    onSuccess: () => {
      Toaster.success(t('components.document-menu.document-list.item.feedback-message.edit.success'));
      refetchDocuments(queryClient);
    },
    onError: () => {
      Toaster.error(t('components.document-menu.document-list.item.feedback-message.edit.error'));
    },
  });
};

export const useRemoveSentDocumentsHistory = () => {
  return useMutation({
    mutationFn: () => {
      return removeSentDocumentsHistory();
    },
  });
};

export const useDocumentAttachments = (documentId: string) => {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'attachments', documentId],
    queryFn: () => {
      return getAttachments(documentId);
    },
  });
};

type AddDocumentAttachmentParams = {
  documentId: string;
  fieldId: string;
  newAttachment: File;
};

export const useAddDocumentAttachment = () => {
  return useMutation({
    mutationFn: async ({documentId, fieldId, newAttachment}: AddDocumentAttachmentParams) => {
      const fileIsImage = newAttachment.type.startsWith('image/');
      const fileIsJson = newAttachment.name.endsWith('.json');
      const fileIsPdf = newAttachment.name.endsWith('.pdf');

      const fileType = fileIsImage
        ? DocumentAttachmentType.image
        : fileIsJson
          ? DocumentAttachmentType.json
          : fileIsPdf
            ? DocumentAttachmentType.pdf
            : DocumentAttachmentType.unknown;

      const extension = newAttachment.name.split('.').pop();
      if (!extension) {
        throw new FileHasNoExtensionError(newAttachment.name);
      }

      const mappedFieldId = mapAttachmentIdFromFieldId(fieldId);
      const fileName = `${mappedFieldId}.${extension}`;

      const attachmentUrlResponse = await createDocumentAttachmentUploadUrl(documentId, {
        fieldId: mappedFieldId,
        fileName,
        type: fileType,
        extension,
      });

      try {
        const attachmentUploadResponse = await fetch(attachmentUrlResponse.data.url, {
          method: 'put',
          body: newAttachment,
        });

        if (attachmentUploadResponse.status !== HttpStatusCode.Ok) {
          throw new Error('Upload failed');
        }

        return attachmentUrlResponse.data.id;
      } catch (error) {
        // Delete the attachment in the API, the upload failed
        await removeDocumentAttachment(documentId, attachmentUrlResponse.data.id);

        throw error;
      }
    },
    onSuccess: (_data, {documentId, newAttachment}) => {
      analyticsHandler.webUpload({
        file_id: documentId,
        file_extension: getFileExtension(newAttachment.name),
      });
    },
  });
};

class FileHasNoExtensionError extends Error {
  constructor(fileName: string) {
    super();
    this.message = `File "${fileName}" has no extension`;
  }
}

type DocumentAttachmentParams = {
  documentId: string;
  attachmentId: string;
};

export const useDownloadDocumentAttachment = () => {
  return useMutation({
    mutationFn: ({documentId, attachmentId}: DocumentAttachmentParams) => {
      return downloadAttachment(documentId, attachmentId);
    },
  });
};

export const useRemoveDocumentAttachment = () => {
  return useMutation({
    mutationFn: ({documentId, attachmentId}: DocumentAttachmentParams) => {
      return removeDocumentAttachment(documentId, attachmentId);
    },
  });
};

type GenerateDocumentFormParams = {
  documentId: string;
  payload: DocumentFormPayload;
};

export const useGenerateDocumentForm = () => {
  return useMutation({
    mutationFn: ({documentId, payload}: GenerateDocumentFormParams) => {
      return generateForm(documentId, payload);
    },
  });
};

export const useDocumentForm = (documentId: string, formId?: string) => {
  return useQuery({
    queryKey: ['documents', 'forms', documentId, formId],
    queryFn: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return getForm(documentId, formId!);
    },
    enabled: !!formId,
  });
};

type DocumentFormParams = {
  documentId: string;
  formId: string;
};

export const useDeleteDocumentForm = () => {
  return useMutation({
    mutationFn: ({documentId, formId}: DocumentFormParams) => {
      return removeForm(documentId, formId);
    },
  });
};
